"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsGetAdSetDetails = exports.facebookAdsGetAdSetDetailsResultSchema = exports.facebookAdsGetAdSetDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var facebookAdsGetAdSetDetailsInputSchema = zod_1.z
    .object({
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific campaign ID to filter by. If provided, returns ad sets only from this campaign.'),
    adSetIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Optional array of specific ad set IDs to analyze. If not provided, returns data for all ad sets in the account/campaign. e.g. ["123456789", "987654321"]'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-01-01. If omitted, defaults to one year ago.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-01-31. If omitted, defaults to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of ad sets to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    breakdowns: zod_1.z
        .array(zod_1.z.enum([
        'age',
        'gender',
        'country',
        'region',
        'device_platform',
        'publisher_platform',
        'platform_position',
    ]))
        .nullish()
        .describe('Optional breakdowns to segment the data. e.g. ["age", "gender"] for demographic analysis. Note: Using multiple breakdowns or demographic breakdowns (age/gender) will exclude conversion metrics due to Facebook API restrictions.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no ad set data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
        includeInactive: zod_1.z
            .boolean()
            .default(false)
            .describe('Include inactive ad sets. Unless specified, defaults to false.'),
        includeInsights: zod_1.z
            .boolean()
            .default(false)
            .describe('MUST be explicitly set to true for performance queries. Controls whether detailed insights array is included in response. When true: returns metadata + summary + insights array with granular per-entity metrics (impressions, clicks, spend, CTR, CPC, CPM by ad set/day/segment). When false: returns ONLY metadata + aggregated summary, insights array is completely omitted. Required for ANY performance analysis, comparisons, or detailed data.'),
    })
        .describe('Additional options for the request')
        .default({}),
})
    .describe('Parameters for fetching Facebook Ads ad set details with metadata and performance insights.');
exports.facebookAdsGetAdSetDetailsArgsSchema = zod_1.z.object({
    args: facebookAdsGetAdSetDetailsInputSchema,
    emitMode: common_1.emitModeSchema,
});
exports.facebookAdsGetAdSetDetailsResultSchema = zod_1.z.object({
    adSets: zod_1.z
        .array(zod_1.z.object({
        adset_id: zod_1.z.string(),
        adset_name: zod_1.z.string(),
        campaign_id: zod_1.z.string().nullish(),
        status: zod_1.z.string().nullish(),
        created_time: zod_1.z.string().nullish(),
        updated_time: zod_1.z.string().nullish(),
        start_time: zod_1.z.string().nullish(),
        end_time: zod_1.z.string().nullish(),
        daily_budget: zod_1.z.string().nullish(),
        lifetime_budget: zod_1.z.string().nullish(),
        targeting: zod_1.z
            .object({
            age_min: zod_1.z.number().nullish(),
            age_max: zod_1.z.number().nullish(),
            genders: zod_1.z.array(zod_1.z.number()).nullish(),
            geo_locations: zod_1.z.any().nullish(),
        })
            .nullish(),
        insights: zod_1.z
            .array(zod_1.z.object({
            adset_id: zod_1.z.string().nullish(),
            adset_name: zod_1.z.string().nullish(),
            campaign_id: zod_1.z.string().nullish(),
            campaign_name: zod_1.z.string().nullish(),
            date_start: zod_1.z.string().nullish(),
            date_stop: zod_1.z.string().nullish(),
            impressions: zod_1.z.string().nullish(),
            clicks: zod_1.z.string().nullish(),
            spend: zod_1.z.string().nullish(),
            reach: zod_1.z.string().nullish(),
            frequency: zod_1.z.string().nullish(),
            ctr: zod_1.z.string().nullish(),
            cpc: zod_1.z.string().nullish(),
            cpm: zod_1.z.string().nullish(),
            cpp: zod_1.z.string().nullish(),
            conversions: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            cost_per_conversion: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            // Raw Meta verification data
            meta_purchase_roas: zod_1.z
                .array(zod_1.z.object({
                action_type: zod_1.z.string(),
                value: zod_1.z.string(),
            }))
                .nullish(),
            meta_action_values: zod_1.z
                .array(zod_1.z.object({
                action_type: zod_1.z.string(),
                value: zod_1.z.string(),
            }))
                .nullish(),
            meta_actions: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            meta_cost_per_action: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            meta_reported_revenue: zod_1.z.number().nullish(),
            meta_reported_roas: zod_1.z.number().nullish(),
            // Breakdown fields (when breakdowns are requested)
            age: zod_1.z.string().nullish(),
            gender: zod_1.z.string().nullish(),
            country: zod_1.z.string().nullish(),
            region: zod_1.z.string().nullish(),
            device_platform: zod_1.z.string().nullish(),
            publisher_platform: zod_1.z.string().nullish(),
            platform_position: zod_1.z.string().nullish(),
        }))
            .nullish(),
    }))
        .describe('List of ad sets with metadata. Each ad set contains insights array when options.includeInsights is true. When false, insights field is omitted. Summary statistics are always provided.'),
    summary: zod_1.z
        .object({
        total_adsets: zod_1.z.number(),
        total_spend: zod_1.z.number().nullish(),
        total_impressions: zod_1.z.number().nullish(),
        total_clicks: zod_1.z.number().nullish(),
        total_reach: zod_1.z.number().nullish(),
        total_revenue: zod_1.z.number().nullish(),
        total_conversions: zod_1.z.number().nullish(),
        roas: zod_1.z.number().nullish(),
        meta_roas: zod_1.z.number().nullish(),
        average_cost_per_conversion: zod_1.z.number().nullish(),
        average_ctr: zod_1.z.number().nullish(),
        average_cpc: zod_1.z.number().nullish(),
        average_cpm: zod_1.z.number().nullish(),
        date_range: zod_1.z.object({
            start: zod_1.z.string(),
            end: zod_1.z.string(),
        }),
        top_performing_adset: zod_1.z
            .object({
            id: zod_1.z.string(),
            name: zod_1.z.string(),
            metric: zod_1.z.string(),
            value: zod_1.z.number(),
        })
            .nullish(),
    })
        .describe('Summary statistics across all ad sets'),
});
exports.facebookAdsGetAdSetDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'facebookAdsGetAdSetDetails',
    displayName: 'Get Facebook Ad Set Details',
    displayDescription: 'Get comprehensive ad set details including metadata and performance insights. Returns ad set metadata and summary statistics. Set options.includeInsights to true to include per-ad-set metrics. Query specific ad sets via adSetIds or omit for all.',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsGetAdSetDetailsArgsSchema,
}, exports.facebookAdsGetAdSetDetailsResultSchema);
